import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import type { AppModel } from '@/models/AppModel';
import type { LogicNodeInstance } from '@/models/logic/LogicNodeInstance';
import { watchActiveExtensionNodes, type ActiveExtensionNode } from './activeNodes';

/** Observe settled output edges only for live extension nodes, including paused Steps. */
export function watchExtensionOutputs(
  app: AppModel,
  type: string,
  change: (target: ActiveExtensionNode, high: boolean) => void,
) {
  let unsubscribers: (() => void)[] = [];
  let previous = new Map<LogicNodeInstance, boolean>();
  return watchActiveExtensionNodes(app, type, (targets) => {
    for (const unsubscribe of unsubscribers) unsubscribe();
    unsubscribers = [];
    const retained = new Map<LogicNodeInstance, boolean>();
    const groups = new Map<
      ActiveExtensionNode['simulation'],
      Map<NodeHandle, ActiveExtensionNode>
    >();
    for (const target of targets) {
      retained.set(target.node, previous.get(target.node) ?? target.node.output);
      let nodes = groups.get(target.simulation);
      if (!nodes) groups.set(target.simulation, (nodes = new Map()));
      nodes.set(target.node.id, target);
    }
    previous = retained;
    for (const [simulation, nodes] of groups) {
      unsubscribers.push(
        simulation.subscribeProcessed((ids) => {
          const candidates = ids.size < nodes.size ? ids : nodes.keys();
          for (const id of candidates) {
            if (!ids.has(id)) continue;
            const target = nodes.get(id);
            if (!target || previous.get(target.node) === target.node.output) continue;
            previous.set(target.node, target.node.output);
            change(target, target.node.output);
          }
        }),
      );
    }
  });
}
